// src/components/admin/attendance/AttendanceCalendarEnhanced.js
import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Users, CheckCircle, XCircle, Calendar, PartyPopper, Home } from 'lucide-react';
import { api } from '../../../services/api';
import holidayPlanService from '../../../services/holidayPlan.service';
import Tooltip from '../../common/Tooltip';
import { toISTDateString, formatISTDate, formatDate, getISTComponents } from '../../../utils/dateHelpers';

const AttendanceCalendarEnhanced = ({ selectedDate, onDateSelect, totalEmployees, onMonthDataLoaded, cachedData, allEmployees }) => {
    const [currentMonth, setCurrentMonth] = useState(new Date(selectedDate));
    const [monthlyAttendance, setMonthlyAttendance] = useState({});
    const [monthlyDetailedData, setMonthlyDetailedData] = useState({}); // Store complete detailed data per day
    const [loading, setLoading] = useState(false);
    const [cacheKey, setCacheKey] = useState(''); // Track which month data is cached

    // View toggles for what to show in cells
    const [viewConfig, setViewConfig] = useState({
        showTotal: true,
        showPresent: true,
        showAbsent: true,
        showLeave: true,
        showHoliday: true,
        showWeekOff: true
    });

    const selectedDateObj = new Date(selectedDate);

    // Helper function to format date in IST timezone (YYYY-MM-DD)
    const formatDateLocal = (date) => {
        // Use IST to ensure consistency with backend data
        return toISTDateString(date);
    };

    // Helper function to parse weekly off string into day numbers
    const parseWeeklyOff = (weeklyOffStr) => {
        if (!weeklyOffStr) return [];

        const weeklyOffStr_lower = String(weeklyOffStr).toLowerCase();
        const dayMap = {
            'sunday': 0, 'sundays': 0, 'sun': 0,
            'monday': 1, 'mondays': 1, 'mon': 1,
            'tuesday': 2, 'tuesdays': 2, 'tue': 2, 'tues': 2,
            'wednesday': 3, 'wednesdays': 3, 'wed': 3,
            'thursday': 4, 'thursdays': 4, 'thu': 4, 'thur': 4, 'thurs': 4,
            'friday': 5, 'fridays': 5, 'fri': 5,
            'saturday': 6, 'saturdays': 6, 'sat': 6
        };

        // Remove common non-day words (only, off, day, days)
        const cleanedStr = weeklyOffStr_lower
            .replace(/\b(only|off|day|days)\b/g, '') // Remove filler words
            .replace(/\s+/g, ' ')  // Normalize spaces
            .trim();

        // Split by comma, ampersand, "and", or spaces
        const parts = cleanedStr.split(/[,&\s]|and/).map(day => day.trim()).filter(day => day);

        // Map to day numbers and remove duplicates
        const dayNumbers = parts
            .map(day => dayMap[day])
            .filter(day => day !== undefined);

        return [...new Set(dayNumbers)];
    };

    // Helper function to check if a date is a week-off for an employee (using IST day of week)
    const isWeekOff = (date, employee) => {
        if (!employee || !employee.weekly_off) return false;
        const weeklyOffDays = parseWeeklyOff(employee.weekly_off);
        const { dayOfWeek } = getISTComponents(date);
        return weeklyOffDays.includes(dayOfWeek);
    };

    // Get normalized employee ID
    const getEmployeeId = (record) => {
        return record.employee_id || record.id || record.emp_id;
    };

    // Status priority for conflict resolution (higher number = higher priority)
    const STATUS_PRIORITY = {
        'inactive': 7,  // Highest priority - can't be overridden
        'leave': 6,      // Approved leave is high priority
        'holiday': 5,    // Company holiday
        'week_off': 4,   // Regular day off
        'present': 3,    // Actually worked
        'half-day': 2,   // Half day
        'absent': 1      // Lowest priority
    };

    useEffect(() => {
        // Only fetch if totalEmployees is available and allEmployees is loaded
        if (totalEmployees > 0 && allEmployees && allEmployees.length > 0) {
            const year = currentMonth.getFullYear();
            const month = currentMonth.getMonth();
            const monthKey = `${year}-${month}`;

            // Check if we have cached data for this month
            if (cachedData && cachedData.summary && cachedData.cacheKey === monthKey) {
                console.log('📦 Using cached calendar data for:', year, month + 1);
                setMonthlyAttendance(cachedData.summary);
                setMonthlyDetailedData(cachedData.detailed || {});
                setCacheKey(monthKey);
            } else {
                console.log('🔄 No cache found, fetching fresh data');
                fetchMonthlyAttendance();
            }
        }
    }, [currentMonth, totalEmployees, cachedData, allEmployees]);

    const fetchMonthlyAttendance = async () => {
        try {
            setLoading(true);
            const year = currentMonth.getFullYear();
            const month = currentMonth.getMonth();
            const monthKey = `${year}-${month}`;

            console.log('📅 Fetching attendance for month:', year, month + 1);

            // Get all days in the current month
            const daysInMonth = new Date(year, month + 1, 0).getDate();
            const grouped = {};

            // Fetch all leave requests once
            const leaveResponse = await api.get(`/api/leaves/all-requests`);
            const allLeaves = leaveResponse.data.leaves || leaveResponse.data.requests || [];

            console.log('📋 All leaves:', allLeaves.length);
            console.log('🔍 DEBUG 1: API Response Structure:', leaveResponse.data);
            console.log('🔍 DEBUG 1: First leave object:', allLeaves[0]);
            console.log('🔍 DEBUG 1: Sample leave fields:', allLeaves[0] ? {
                id: allLeaves[0].id,
                employee_id: allLeaves[0].employee_id,
                status: allLeaves[0].status,
                from_date: allLeaves[0].from_date,
                to_date: allLeaves[0].to_date,
                employee_name: allLeaves[0].employee_name,
                employee_code: allLeaves[0].employee_code
            } : 'No leaves found');

            // Get unique holiday plan IDs from employees
            const uniquePlanIds = [...new Set(
                allEmployees
                    .map(emp => emp.holiday_plan_id)
                    .filter(id => id != null)
            )];

            console.log('🎉 Unique holiday plans found:', uniquePlanIds.length, uniquePlanIds);

            // Fetch holidays for each unique plan
            const holidaysByPlan = {}; // planId -> holidays[]
            await Promise.all(
                uniquePlanIds.map(async (planId) => {
                    try {
                        const plan = await holidayPlanService.getHolidayPlanById(planId, year);
                        if (plan && plan.holidays) {
                            // Filter to current month
                            holidaysByPlan[planId] = plan.holidays.filter(h => {
                                const [hYear, hMonth] = h.date.split('-').map(Number);
                                return hYear === year && (hMonth - 1) === month;
                            });
                            console.log(`   Plan ${planId} has ${holidaysByPlan[planId].length} holidays in this month`);
                        }
                    } catch (err) {
                        console.error(`Error fetching holiday plan ${planId}:`, err);
                        holidaysByPlan[planId] = [];
                    }
                })
            );

            console.log('🎉 Fetched holidays for', Object.keys(holidaysByPlan).length, 'plans');

            // Fetch approved optional holiday requests for the month
            // This creates a map: employeeId -> Set of approved holiday_date_ids
            const approvedOptionalByEmployee = {};
            try {
                const approvedRequests = await holidayPlanService.getOptionalHolidayRequests('approved');
                approvedRequests.forEach(request => {
                    const empId = request.employee_id;
                    if (!approvedOptionalByEmployee[empId]) {
                        approvedOptionalByEmployee[empId] = new Set();
                    }
                    approvedOptionalByEmployee[empId].add(request.holiday_date_id);
                });
                console.log('🎯 Approved optional holidays by employee:', Object.keys(approvedOptionalByEmployee).length, 'employees');
            } catch (err) {
                console.warn('Could not fetch approved optional holidays:', err);
            }

            // Helper to check if a holiday applies to an employee (based on their assigned plan)
            const isHolidayForEmployee = (dateStr, employeeId) => {
                // Find the employee to get their holiday_plan_id
                const employee = allEmployees.find(e => getEmployeeId(e) === employeeId);
                if (!employee || !employee.holiday_plan_id) return false;

                // Get holidays from employee's specific plan
                const planHolidays = holidaysByPlan[employee.holiday_plan_id] || [];
                const holiday = planHolidays.find(h => toISTDateString(h.date) === dateStr);

                if (!holiday) return false;

                // Check if it's an optional holiday (support both is_optional flag and holiday_type)
                const isOptional = holiday.is_optional || holiday.holiday_type === 'optional';

                // Non-optional holidays from employee's plan apply automatically
                if (!isOptional) return true;

                // Optional holidays only apply if employee has approved request
                const employeeApprovedSet = approvedOptionalByEmployee[employeeId];
                return employeeApprovedSet && employeeApprovedSet.has(holiday.id);
            };

            // Fetch attendance for each day of the month
            const attendancePromises = [];
            for (let day = 1; day <= daysInMonth; day++) {
                const date = new Date(year, month, day);
                const dateStr = formatDateLocal(date); // Use local formatting to avoid timezone shift
                attendancePromises.push(
                    api.get(`/admin/attendance?date=${dateStr}`)
                        .then(response => ({
                            date: dateStr,
                            data: response.data.attendance || []
                        }))
                        .catch(error => {
                            console.error(`Error fetching attendance for ${dateStr}:`, error);
                            return { date: dateStr, data: [] };
                        })
                );
            }

            // Wait for all attendance data
            const attendanceResults = await Promise.all(attendancePromises);

            console.log('📊 Fetched attendance for', attendanceResults.length, 'days');

            // Store detailed data for each day using Map-based deduplication
            const detailedData = {};

            // Process attendance data with deduplication for each date
            attendanceResults.forEach(({ date, data }) => {
                // Create a Map for each date to ensure no duplicates
                const employeeMapForDate = new Map();
                // date is already in YYYY-MM-DD IST format from formatDateLocal

                // Initialize all employees for this date
                allEmployees.forEach(emp => {
                    const empId = getEmployeeId(emp);
                    // Compare dates using IST date strings to avoid timezone issues
                    // Pass the raw string to toISTDateString to handle it correctly
                    const joiningDateStr = emp.joining_date ? toISTDateString(emp.joining_date) : null;

                    // Determine initial status
                    let initialStatus = 'absent';
                    // Compare as strings (YYYY-MM-DD format allows string comparison)
                    // Only inactive if date is BEFORE joining date (not on or after)
                    if (joiningDateStr && date < joiningDateStr) {
                        initialStatus = 'inactive';
                    }

                    // Check for holiday/week_off if not already inactive
                    // NOTE: For holidays, check if it applies to this specific employee
                    // (optional holidays only apply if employee has approved request)
                    if (initialStatus !== 'inactive') {
                        if (isHolidayForEmployee(date, empId)) {
                            initialStatus = 'holiday';
                        } else if (isWeekOff(new Date(date + 'T00:00:00+05:30'), emp)) {
                            // Create date with IST timezone to ensure correct day of week
                            initialStatus = 'week_off';
                        }
                    }

                    employeeMapForDate.set(empId, {
                        employee_id: empId,
                        id: empId,
                        employee_code: emp.employee_code,
                        first_name: emp.first_name,
                        last_name: emp.last_name,
                        department_name: emp.department_name,
                        status: initialStatus,
                        check_in_time: null,
                        check_out_time: null,
                        work_hours: 0,
                        work_location: null
                    });
                });

                // Process attendance data (present employees)
                data.forEach(record => {
                    const empId = getEmployeeId(record);
                    const existing = employeeMapForDate.get(empId);

                    if (existing && existing.status !== 'inactive') {
                        // Present status can override holiday/week-off/absent
                        employeeMapForDate.set(empId, {
                            ...existing,
                            ...record,
                            status: 'present'
                        });
                    }
                });

                // Store the deduplicated data for this date
                detailedData[date] = Array.from(employeeMapForDate.values());

                // Initialize grouped stats (will be updated with leaves later)
                grouped[date] = {
                    present: 0,
                    absent: 0,
                    leave: 0,
                    holiday: 0,
                    weekOff: 0,
                    inactive: 0,
                    total: totalEmployees
                };
            });

            // Process leave data - update existing employee records with deduplication
            console.log('🔍 DEBUG 2: Processing approved leaves with deduplication');
            let approvedCount = 0;

            allLeaves.forEach(leave => {
                if (leave.status !== 'approved') return;

                // Skip if missing required date fields
                if (!leave.from_date || !leave.to_date) {
                    console.warn('Leave missing date fields:', leave);
                    return;
                }

                approvedCount++;
                const empId = getEmployeeId(leave);

                // Get IST date strings for leave period
                // Handle both date-only strings (YYYY-MM-DD) and full timestamps
                const leaveStartStr = toISTDateString(new Date(leave.from_date));
                const leaveEndStr = toISTDateString(new Date(leave.to_date));

                // Skip if dates are invalid
                if (!leaveStartStr || !leaveEndStr || leaveStartStr === 'Invalid Date' || leaveEndStr === 'Invalid Date') {
                    console.warn('Invalid leave dates:', leave.from_date, leave.to_date);
                    return;
                }

                // Parse employee name
                const nameParts = (leave.employee_name || '').split(' ');
                const firstName = leave.first_name || nameParts[0] || '';
                const lastName = leave.last_name || nameParts.slice(1).join(' ') || '';

                // Iterate through each day of the leave period using date strings
                // This avoids timezone issues by working with IST date strings directly
                let currentDateStr = leaveStartStr;
                while (currentDateStr <= leaveEndStr) {
                    // Check if this date exists in our detailedData (i.e., within current month)
                    const dayData = detailedData[currentDateStr];
                    if (dayData) {
                        // Find the employee in the deduplicated data
                        const empIndex = dayData.findIndex(e => getEmployeeId(e) === empId);

                        if (empIndex !== -1) {
                            // Leave overrides most statuses except inactive
                            if (dayData[empIndex].status !== 'inactive') {
                                dayData[empIndex] = {
                                    ...dayData[empIndex],
                                    status: 'leave',
                                    leave_type: leave.leave_type_name || leave.leave_type,
                                    leave_reason: leave.reason,
                                    first_name: firstName || dayData[empIndex].first_name,
                                    last_name: lastName || dayData[empIndex].last_name,
                                    department_name: leave.department_name || dayData[empIndex].department_name
                                };
                            }
                        } else {
                            console.warn(`Leave record for unknown employee ID: ${empId} on ${currentDateStr}`);
                        }
                    }

                    // Move to next day by parsing date string, adding 1 day, and converting back
                    const [y, m, d] = currentDateStr.split('-').map(Number);
                    const nextDate = new Date(y, m - 1, d + 1);
                    currentDateStr = toISTDateString(nextDate);
                }
            });

            console.log(`✅ Processed ${approvedCount} approved leaves with deduplication`);

            console.log('🔍 DEBUG 2: Leave processing complete');
            console.log('   - Total leaves:', allLeaves.length);
            console.log('   - Approved leaves:', approvedCount);

            // Calculate stats for each day from the deduplicated data
            console.log('📊 Calculating stats from deduplicated data');

            Object.keys(detailedData).forEach(dateStr => {
                const dayData = detailedData[dateStr] || [];

                // Initialize stats for this date
                const stats = {
                    total: 0,
                    present: 0,
                    absent: 0,
                    leave: 0,
                    holiday: 0,
                    weekOff: 0,
                    inactive: 0
                };

                // Count each status
                dayData.forEach(emp => {
                    if (emp.status === 'inactive') {
                        stats.inactive++;
                    } else {
                        stats.total++; // Only count active employees in total

                        switch(emp.status) {
                            case 'present':
                                stats.present++;
                                break;
                            case 'absent':
                                stats.absent++;
                                break;
                            case 'leave':
                                stats.leave++;
                                break;
                            case 'holiday':
                                stats.holiday++;
                                break;
                            case 'week_off':
                                stats.weekOff++;
                                break;
                        }
                    }
                });

                // Update grouped stats with accurate counts
                grouped[dateStr] = {
                    present: stats.present,
                    absent: stats.absent,
                    leave: stats.leave,
                    holiday: stats.holiday,
                    weekOff: stats.weekOff,
                    inactive: stats.inactive,
                    total: stats.total
                };

                // Validation check
                const sumCheck = stats.present + stats.absent + stats.leave + stats.holiday + stats.weekOff;
                if (sumCheck !== stats.total) {
                    console.error(`⚠️ Stats validation failed for ${dateStr}:`, {
                        sum: sumCheck,
                        total: stats.total,
                        breakdown: stats
                    });
                }
            });


            console.log('✅ Monthly attendance grouped:', grouped);

            // DEBUG 5: Show sample dates with leave counts
            console.log('🔍 DEBUG 5: Final grouped object analysis');
            const datesWithLeaves = Object.keys(grouped).filter(date => grouped[date].leave > 0);
            console.log(`   - Total dates in month: ${Object.keys(grouped).length}`);
            console.log(`   - Dates with leave count > 0: ${datesWithLeaves.length}`);
            if (datesWithLeaves.length > 0) {
                console.log('   - Sample dates with leaves:');
                datesWithLeaves.slice(0, 3).forEach(date => {
                    console.log(`      ${date}: leave=${grouped[date].leave}, present=${grouped[date].present}, absent=${grouped[date].absent}, total=${grouped[date].total}`);
                    console.log(`      DetailedData records: ${detailedData[date]?.length || 0}`);
                    const leaveRecords = detailedData[date]?.filter(r => r.status === 'leave') || [];
                    console.log(`      Leave records: ${leaveRecords.length}`, leaveRecords.map(r => `${r.first_name} ${r.last_name}`.trim() || 'Unknown'));
                });
            } else {
                console.warn('   ⚠️ WARNING: No dates have leave count > 0!');
            }

            setMonthlyAttendance(grouped);
            setMonthlyDetailedData(detailedData);
            setCacheKey(monthKey);

            // Pass both summary and detailed data to parent component for instant switching
            if (onMonthDataLoaded) {
                onMonthDataLoaded({
                    summary: grouped,      // Stats for each day
                    detailed: detailedData, // Complete employee list for each day
                    leaves: allLeaves,     // All leave data
                    cacheKey: monthKey     // Month identifier for cache validation
                });
            }
        } catch (error) {
            console.error('❌ Error fetching monthly attendance:', error);
            setMonthlyAttendance({});
        } finally {
            setLoading(false);
        }
    };

    const getDaysInMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDayOfWeek = firstDay.getDay();

        const days = [];

        // Add empty cells for days before month starts
        for (let i = 0; i < startingDayOfWeek; i++) {
            days.push(null);
        }

        // Add all days of the month
        for (let day = 1; day <= daysInMonth; day++) {
            days.push(new Date(year, month, day));
        }

        return days;
    };

    const goToPreviousMonth = () => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
    };

    const goToNextMonth = () => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
    };

    const goToToday = () => {
        const today = new Date();
        setCurrentMonth(today);
        // Use IST date string to ensure correct date selection
        onDateSelect(toISTDateString(today));
    };

    const isToday = (date) => {
        if (!date) return false;
        // Compare using IST date strings to avoid timezone issues
        return toISTDateString(date) === toISTDateString(new Date());
    };

    const isSelected = (date) => {
        if (!date) return false;
        // Compare using IST date strings
        return toISTDateString(date) === selectedDate;
    };

    const getAttendanceForDate = (date) => {
        if (!date) return null;
        const dateStr = formatDateLocal(date); // Use local formatting
        return monthlyAttendance[dateStr] || null;
    };

    const toggleView = (key) => {
        setViewConfig(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const selectAllDisplayOptions = () => {
        setViewConfig({
            showTotal: true,
            showPresent: true,
            showAbsent: true,
            showLeave: true,
            showHoliday: true,
            showWeekOff: true
        });
    };

    const clearAllDisplayOptions = () => {
        setViewConfig({
            showTotal: false,
            showPresent: false,
            showAbsent: false,
            showLeave: false,
            showHoliday: false,
            showWeekOff: false
        });
    };

    const days = getDaysInMonth(currentMonth);
    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    return (
        <div className="tour-attendance-calendar-panel bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Attendance Calendar
                </h3>
                <button
                    onClick={goToToday}
                    className="tour-attendance-calendar-today px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                    Today
                </button>
            </div>

            {/* Month Navigation */}
            <div className="flex items-center justify-between mb-4">
                <button
                    onClick={goToPreviousMonth}
                    className="tour-attendance-calendar-prev-month p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                >
                    <ChevronLeft className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                </button>
                <h4 className="tour-attendance-calendar-month-label text-lg font-semibold text-gray-900 dark:text-white">
                    {currentMonth.toLocaleDateString('en-IN', { month: 'long', year: 'numeric', timeZone: 'Asia/Kolkata' })}
                </h4>
                <button
                    onClick={goToNextMonth}
                    className="tour-attendance-calendar-next-month p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                >
                    <ChevronRight className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                </button>
            </div>

            {/* View Configuration */}
            <div className="tour-attendance-calendar-display-options mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded">
                <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">Display Options:</div>
                    <div className="flex gap-2">
                        <button
                            onClick={selectAllDisplayOptions}
                            className="px-2 py-1 text-xs font-medium bg-blue-600 text-white rounded hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 transition-colors"
                            title="Select all display options"
                        >
                            Select All
                        </button>
                        <button
                            onClick={clearAllDisplayOptions}
                            className="px-2 py-1 text-xs font-medium bg-gray-600 text-white rounded hover:bg-gray-700 dark:bg-gray-500 dark:hover:bg-gray-600 transition-colors"
                            title="Clear all display options"
                        >
                            Clear All
                        </button>
                    </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                    <label className="flex items-center gap-2 cursor-pointer" title="Show/hide total active employees count in calendar cells">
                        <input
                            type="checkbox"
                            checked={viewConfig.showTotal}
                            onChange={() => toggleView('showTotal')}
                            className="w-4 h-4"
                        />
                        <Users className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                        <span className="text-sm text-gray-900 dark:text-white">Total</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer" title="Show/hide present employees count (marked attendance)">
                        <input
                            type="checkbox"
                            checked={viewConfig.showPresent}
                            onChange={() => toggleView('showPresent')}
                            className="w-4 h-4"
                        />
                        <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                        <span className="text-sm text-gray-900 dark:text-white">Present</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer" title="Show/hide absent employees count (did not mark attendance)">
                        <input
                            type="checkbox"
                            checked={viewConfig.showAbsent}
                            onChange={() => toggleView('showAbsent')}
                            className="w-4 h-4"
                        />
                        <XCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
                        <span className="text-sm text-gray-900 dark:text-white">Absent</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer" title="Show/hide employees on approved leave">
                        <input
                            type="checkbox"
                            checked={viewConfig.showLeave}
                            onChange={() => toggleView('showLeave')}
                            className="w-4 h-4"
                        />
                        <Calendar className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                        <span className="text-sm text-gray-900 dark:text-white">Leave</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer" title="Show/hide employees on public holidays">
                        <input
                            type="checkbox"
                            checked={viewConfig.showHoliday}
                            onChange={() => toggleView('showHoliday')}
                            className="w-4 h-4"
                        />
                        <PartyPopper className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                        <span className="text-sm text-gray-900 dark:text-white">Holiday</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer" title="Show/hide employees on their weekly off day">
                        <input
                            type="checkbox"
                            checked={viewConfig.showWeekOff}
                            onChange={() => toggleView('showWeekOff')}
                            className="w-4 h-4"
                        />
                        <Home className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                        <span className="text-sm text-gray-900 dark:text-white">Week Off</span>
                    </label>
                </div>
            </div>

            {/* Loading Indicator */}
            {loading && (
                <div className="text-center py-2 text-sm text-blue-600 dark:text-blue-400">
                    Loading calendar data...
                </div>
            )}

            {/* Calendar Grid */}
            <div className="tour-attendance-calendar-grid grid grid-cols-7 gap-2">
                {/* Week day headers */}
                {weekDays.map(day => (
                    <div
                        key={day}
                        className="text-center text-xs font-semibold text-gray-600 dark:text-gray-400 py-2"
                    >
                        {day}
                    </div>
                ))}

                {/* Calendar days */}
                {days.map((date, index) => {
                    const attendance = getAttendanceForDate(date);
                    const today = isToday(date);
                    const selected = isSelected(date);
                    const isWeekend = date && (getISTComponents(date).dayOfWeek === 0 || getISTComponents(date).dayOfWeek === 6);

                    return (
                        <div
                            key={index}
                            onClick={() => date && onDateSelect(formatDateLocal(date))}
                            className={`
                                tour-attendance-calendar-day min-h-[100px] p-2 rounded-lg border-2 transition-all cursor-pointer
                                ${!date ? 'invisible' : ''}
                                ${selected
                                    ? 'tour-attendance-calendar-day-selected border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                                    : 'border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-700'
                                }
                                ${today ? 'ring-2 ring-blue-400' : ''}
                                ${isWeekend ? 'bg-gray-50 dark:bg-gray-700/30' : 'bg-white dark:bg-gray-800'}
                            `}
                        >
                            {date && (
                                <div className="flex flex-col h-full">
                                    {/* Date number */}
                                    <div className={`
                                        text-sm font-semibold mb-2
                                        ${today ? 'text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-white'}
                                    `}>
                                        {date.getDate()}
                                    </div>

                                    {/* Attendance counts */}
                                    {attendance ? (
                                        <div className="flex-1 flex flex-col gap-1 text-xs">
                                            {viewConfig.showTotal && (
                                                <Tooltip content={`Total Active Employees: ${attendance.total}`} position="top">
                                                    <div className="flex items-center gap-1">
                                                        <Users className="w-3 h-3 text-gray-600 dark:text-gray-400" />
                                                        <span className="text-gray-700 dark:text-gray-300">
                                                            {attendance.total}
                                                        </span>
                                                    </div>
                                                </Tooltip>
                                            )}
                                            {viewConfig.showPresent && (
                                                <Tooltip content={`Present: ${attendance.present} employee${attendance.present !== 1 ? 's' : ''} marked attendance`} position="top">
                                                    <div className="flex items-center gap-1">
                                                        <CheckCircle className="w-3 h-3 text-green-600 dark:text-green-400" />
                                                        <span className="text-green-700 dark:text-green-400 font-medium">
                                                            {attendance.present}
                                                        </span>
                                                    </div>
                                                </Tooltip>
                                            )}
                                            {viewConfig.showAbsent && (
                                                <Tooltip content={`Absent: ${attendance.absent} employee${attendance.absent !== 1 ? 's' : ''} did not mark attendance`} position="top">
                                                    <div className="flex items-center gap-1">
                                                        <XCircle className="w-3 h-3 text-red-600 dark:text-red-400" />
                                                        <span className="text-red-700 dark:text-red-400 font-medium">
                                                            {attendance.absent}
                                                        </span>
                                                    </div>
                                                </Tooltip>
                                            )}
                                            {viewConfig.showLeave && attendance.leave > 0 && (
                                                <Tooltip content={`On Leave: ${attendance.leave} employee${attendance.leave !== 1 ? 's' : ''} on approved leave`} position="top">
                                                    <div className="flex items-center gap-1">
                                                        <Calendar className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                                                        <span className="text-blue-700 dark:text-blue-400 font-medium">
                                                            {attendance.leave}
                                                        </span>
                                                    </div>
                                                </Tooltip>
                                            )}
                                            {viewConfig.showHoliday && attendance.holiday > 0 && (
                                                <Tooltip content={`Holiday: ${attendance.holiday} employee${attendance.holiday !== 1 ? 's' : ''} on public holiday`} position="top">
                                                    <div className="flex items-center gap-1">
                                                        <PartyPopper className="w-3 h-3 text-orange-600 dark:text-orange-400" />
                                                        <span className="text-orange-700 dark:text-orange-400 font-medium">
                                                            {attendance.holiday}
                                                        </span>
                                                    </div>
                                                </Tooltip>
                                            )}
                                            {viewConfig.showWeekOff && attendance.weekOff > 0 && (
                                                <Tooltip content={`Week Off: ${attendance.weekOff} employee${attendance.weekOff !== 1 ? 's' : ''} on weekly off`} position="top">
                                                    <div className="flex items-center gap-1">
                                                        <Home className="w-3 h-3 text-purple-600 dark:text-purple-400" />
                                                        <span className="text-purple-700 dark:text-purple-400 font-medium">
                                                            {attendance.weekOff}
                                                        </span>
                                                    </div>
                                                </Tooltip>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="flex-1 flex items-center justify-center">
                                            <span className="text-xs text-gray-400 dark:text-gray-600">
                                                {loading ? '...' : 'No data'}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Legend */}
            <div className="mt-4 pt-3 border-t dark:border-gray-700">
                <div className="flex flex-wrap gap-4 text-xs text-gray-600 dark:text-gray-400">
                    <div className="flex items-center gap-1">
                        <div className="w-3 h-3 rounded border-2 border-blue-500"></div>
                        <span>Selected</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <div className="w-3 h-3 rounded ring-2 ring-blue-400"></div>
                        <span>Today</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <div className="w-3 h-3 rounded bg-gray-50 dark:bg-gray-700"></div>
                        <span>Weekend</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AttendanceCalendarEnhanced;
